import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { get } from "lodash";
import { makeStyles } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/Edit";
import RemoveIcon from "@material-ui/icons/Remove";
import { baseUrl } from "../core/Api";
import { updateField, removeField, updateOverallWidth } from "../reducers/BertArchive/Actions";
import FieldOptionsDialog from "./FieldOptionsDialog";
import arrowUp from "../assets/arrowUp.svg";
import arrowDown from "../assets/arrowDown.svg";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  wrapper: {
    position: "relative",
  },
  flexCol: {
    display: "flex",
    alignItems: "center",
    position: "relative",
  },
  container: {
    position: "relative",
  },
  leftSidePanel: {
    marginTop: 20,
    zIndex: 2,
  },
  rightSidePanel: {
    marginTop: 20,
    zIndex: 2,
  },
  supportBar: {
    position: "absolute",
    zIndex: 2,
  },
  iconButton: {
    backgroundColor: "#c5da86",
    borderWidth: 1,
    borderColor: "#c5da86",
    boxShadow: "0px 2px 4px #00000080",
    borderRadius: 2,
    bottom: "-10px",
    position: "absolute",
    padding: 4,
    zIndex: 15,
    "&:hover": {
      backgroundColor: "#c5da86",
    },
  },
  shelfWidth: {
    bottom: "-80px",
    position: "absolute",
    fontSize: 14,
    fontFamily: theme.typography.fontFamily,
    fontWeight: "normal",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    display: "flex",
  },
  arrow: {
    display: "flex",
    alignItems: "flex-start",
    width: "50%",
    flex: 1,
  },
  shelfHeight: {
    left: 0,
    bottom: -64,
    minHeight: 54,
    position: "absolute",
    fontSize: 14,
    fontFamily: theme.typography.fontFamily,
    fontWeight: "normal",
  },
  deleteButton: {
    backgroundColor: "#788184",
    borderWidth: 1,
    borderColor: "#788184",
    boxShadow: "0px 2px 4px #00000080",
    borderRadius: 2,
    padding: 2,
    top: -60,
    left: "50%",
    position: "absolute",
    "&:hover": {
      backgroundColor: "#788184",
    },
  },
}));

const FrontViewIsoArchive = (data, { ...props }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const optionsLoaded = useSelector((state) => {
    return state.Configuration.optionsLoaded;
  });
  const id = get(data, "data.id", 1);
  const height = Number(get(data, "height", 2000).match(/\d+/));
  const depth = Number(get(data, "depth", 300).match(/\d+/));
  const widthIdentifier = get(data, "data.width", 1900);
  const width = Number(get(data, "data.width", 1900).match(/\d+/));
  const supportbarIdentifier = get(data, "data.layers", 3);
  const supportBars = Number(get(data, "data.layers", 3).match(/\d+/));
  const shelfLoadIdentifier = get(data, "shelfLoad", 250);
  const doubleShelf = get(data, "doubleShelf", 250);
  const leftSidePanel = get(data, "data.leftPanel", false);
  const rightSidePanel = get(data, "data.rightPanel", false);
  const scaleFactor = get(data, "scaleFactor", 8);
  const lastField = get(data, "lastField", false);
  const [showFieldOptions, setShowFieldOptions] = useState(false);
  const defaultShelfBottomHeight = 22;
  const shelfBottomHeight = get(data, "shelfBottomHeight", 22);
  const shelfBottomHeightDifference = shelfBottomHeight - defaultShelfBottomHeight;
  const scaledHeight = height / scaleFactor;
  const scaledWidth = (width + shelfBottomHeightDifference) / scaleFactor;
  const widthContainerMargin = width <= 1005 ? 6 : 12;
  const widthContainerWidth = width < 1005 ? 48 : 54;
  const shelfHeight = Math.floor((height - 75 - supportBars * 70) / (supportBars - 1) / 25) * 25;
  let shelfWidthOffset = 230 / scaleFactor;
  if (depth === 400) {
    shelfWidthOffset = 275 / scaleFactor;
  } else if (depth === 500) {
    shelfWidthOffset = 310 / scaleFactor;
  } else if (depth === 600) {
    shelfWidthOffset = height > 2075 ? 350 / scaleFactor : 345 / scaleFactor;
  }
  const updateWidth = async (width) => {
    await dispatch(updateField(id, "width", width));
    return dispatch(updateOverallWidth());
  };
  const updateLeftSidePanel = (panel) => {
    dispatch(updateField(id, "leftPanel", panel));
  };
  const updateRightSidePanel = (panel) => {
    dispatch(updateField(id, "rightPanel", panel));
  };
  const updateSupportBarsAmount = (amount) => {
    dispatch(updateField(id, "layers", amount));
  };
  const removeCurrentField = () => {
    if (id !== 1 && id !== 2) {
      dispatch(removeField(id));
    }
  };

  const classes = useStyles({ scaleFactor });
  const shelfLoadOptions = useSelector(
    (state) => state.Configuration.bertArchiveComponentOptions.find((opt) => opt.key === "shelfLoad").options
  );
  const selectedShelfLoadOption =
    shelfLoadOptions.length > 0
      ? shelfLoadOptions.find((option) => option.identifier === shelfLoadIdentifier)
      : {};
  const shelfBottomAttributes = selectedShelfLoadOption.attributes
    ? selectedShelfLoadOption.attributes.find((t) => t.identifier === "shelfbottoms").values
    : [];
  const floor = shelfBottomAttributes.length > 0 ? shelfBottomAttributes[0].value : "chipboard";
  const frameAttributes = selectedShelfLoadOption.attributes
    ? selectedShelfLoadOption.attributes.find((t) => t.identifier === "frames").values
    : [];
  const frameModel = frameAttributes.length > 0 ? frameAttributes[0].value : "without_holes";
  const sidePanelAttributes = selectedShelfLoadOption.attributes
    ? selectedShelfLoadOption.attributes.find((t) => t.identifier === "side_singlepanels").values
    : [];
  const sidepanelModel = sidePanelAttributes.length > 0 ? sidePanelAttributes[0].value : "standard";
  const calcHeight = scaledHeight - shelfBottomHeight - 48 / scaleFactor;
  const spacing = calcHeight / (supportBars - 1);
  const rowElements = [];
  for (let i = 0; i < supportBars; i++) {
    rowElements.push(
      <div
        className={classes.supportBar}
        key={`supportBar_${i}`}
        style={{
          minHeight: shelfBottomHeight,
          maxHeight: shelfBottomHeight,
          top: i === 0 ? 0 : i * spacing,
          zIndex: supportBars - i,
          maxWidth: scaledWidth,
        }}
      >
        <img
          onLoad={(e) => {
            if (i === 0 && id === 1) {
              data.saveShelfHeightPerDepth(e.currentTarget.clientHeight);
            }
          }}
          src={`${baseUrl}/images/item/bert/archive/image/shelfbottom_${floor}_width_${width}_depth_${depth}.svg`}
          style={{ maxWidth: scaledWidth, minWidth: scaledWidth }}
        />
      </div>
    );
  }
  return (
    <div
      className={classes.container}
      style={{
        width: scaledWidth + shelfBottomHeightDifference,
        display: "flex",
        flexDirection: "inherit",
        height: "100%",
      }}
    >
      {doubleShelf && (
        <div
          className={classes.flexCol}
          style={{
            width: scaledWidth,
            height: scaledHeight,
            marginLeft: id === 1 ? 0 : -shelfBottomHeight * (id - 1) - shelfBottomHeightDifference * (id - 1),
            left: shelfBottomHeight - 48 / scaleFactor,
            top: -shelfBottomHeight + 64 / scaleFactor,
            zIndex: 4,
            position: "absolute",
          }}
        >
          {id === 1 && (
            <div
              className={classes.leftPanel}
              style={{
                display: "flex",
                position: "absolute",
                top: 0,
                height: scaledHeight - shelfBottomHeight,
              }}
            >
              {!leftSidePanel ? (
                <img
                  src={`${baseUrl}/images/item/bert/archive/image/side_singlepanels_height_${height}_depth_${depth}_${sidepanelModel}.svg`}
                  className={classes.leftSidePanel}
                  style={{
                    maxHeight: scaledHeight - shelfBottomHeight + 24 / scaleFactor,
                    position: "absolute",
                    left: 16 / scaleFactor,
                    marginTop: shelfBottomHeight,
                  }}
                />
              ) : (
                <div
                  className={classes.leftSidePanel}
                  style={{
                    backgroundImage: `url(${baseUrl}/images/item/bert/archive/image/side_fullpanel_height_${height}_depth_${depth}.svg)`,
                    minHeight: scaledHeight,
                    minWidth: shelfBottomHeight,
                    marginTop: 0,
                    backgroundSize: "cover",
                    position: "absolute",
                    left: 16 / scaleFactor,
                  }}
                />
              )}
              <img
                src={`${baseUrl}/images/item/bert/archive/image/frame_height_${height}_${frameModel}.svg`}
                style={{
                  maxHeight: scaledHeight - shelfBottomHeight + 24 / scaleFactor,
                  position: "absolute",
                  left: shelfBottomHeight - 32 / scaleFactor,
                  top: 16 / scaleFactor,
                  display: "block",
                }}
              />
            </div>
          )}
          <div
            className={classes.wrapper}
            style={{
              minHeight: scaledHeight - 48 / scaleFactor,
              maxHeight: scaledHeight,
              minWidth: scaledWidth,
              width: scaledWidth,
            }}
          >
            {rowElements}
          </div>

          <div
            className={classes.rightPanel}
            style={{
              display: "flex",
              position: "absolute",
              top: 0,
              height: scaledHeight - shelfBottomHeight,
            }}
          >
            {!rightSidePanel ? (
              <img
                src={`${baseUrl}/images/item/bert/archive/image/side_singlepanels_height_${height}_depth_${depth}_${sidepanelModel}.svg`}
                className={classes.rightSidePanel}
                style={{
                  maxHeight: scaledHeight - shelfBottomHeight + 24 / scaleFactor,
                  position: "absolute",
                  marginTop: shelfBottomHeight,
                  right: -scaledWidth - 8 / scaleFactor,
                  zIndex: 13,
                }}
              />
            ) : (
              <div
                className={classes.rightSidePanel}
                style={{
                  backgroundImage: `url(${baseUrl}/images/item/bert/archive/image/side_fullpanel_height_${height}_depth_${depth}.svg)`,
                  minHeight: scaledHeight,
                  minWidth: shelfBottomHeight,
                  marginTop: 4,
                  backgroundSize: "cover",
                  position: "absolute",
                  right: -scaledWidth,
                  zIndex: 13,
                }}
              />
            )}
            <img
              src={`${baseUrl}/images/item/bert/archive/image/frame_height_${height}_${frameModel}.svg`}
              className={classes.leftFrame1}
              style={{
                maxHeight: scaledHeight - shelfBottomHeight + 58 / scaleFactor,
                position: "absolute",
                left: scaledWidth - 48 / scaleFactor,
                top: 16 / scaleFactor,
                display: "block",
              }}
            />
          </div>
        </div>
      )}
      <div
        className={classes.flexCol}
        style={{
          width: scaledWidth,
          height: scaledHeight,
          marginLeft: id === 1 ? 0 : -shelfBottomHeight * (id - 1) - shelfBottomHeightDifference * (id - 1),
          zIndex: 6,
          position: "absolute",
          top: 0,
        }}
      >
        {id === 1 && (
          <div
            className={classes.leftPanel}
            style={{
              display: "flex",
              position: "absolute",
              top: 0,
              height: scaledHeight - shelfBottomHeight,
            }}
          >
            <img
              src={`${baseUrl}/images/item/bert/archive/image/frame_height_${height}_${frameModel}.svg`}
              style={{
                maxHeight: scaledHeight - shelfBottomHeight + 64 / scaleFactor,
                position: "absolute",
                left: 0,
                marginTop: shelfBottomHeight - 48 / scaleFactor,
                zIndex: 14,
                display: "block",
              }}
            />
            {!leftSidePanel ? (
              <img
                src={`${baseUrl}/images/item/bert/archive/image/side_singlepanels_height_${height}_depth_${depth}_${sidepanelModel}.svg`}
                className={classes.leftSidePanel}
                style={{
                  maxHeight: scaledHeight - shelfBottomHeight + 24 / scaleFactor,
                  position: "absolute",
                  left: 0,
                  marginTop: shelfBottomHeight,
                }}
              />
            ) : (
              <div
                className={classes.leftSidePanel}
                style={{
                  backgroundImage: `url(${baseUrl}/images/item/bert/archive/image/side_fullpanel_height_${height}_depth_${depth}.svg)`,
                  minHeight: scaledHeight,
                  minWidth: shelfBottomHeight,
                  marginTop: 0,
                  backgroundSize: "cover",
                  position: "absolute",
                  left: 16 / scaleFactor,
                }}
              />
            )}
            <img
              src={`${baseUrl}/images/item/bert/archive/image/frame_height_${height}_${frameModel}.svg`}
              className={classes.leftFrame1}
              style={{
                maxHeight: scaledHeight - shelfBottomHeight + 24 / scaleFactor,
                position: "absolute",
                left: shelfBottomHeight - 64 / scaleFactor,
                top: 16 / scaleFactor,
                display: "block",
              }}
            />
          </div>
        )}
        {id !== 1 && id !== 2 && (
          <IconButton
            className={classes.deleteButton}
            onClick={removeCurrentField}
            style={{ left: `calc(50% - 21px - ${shelfBottomHeightDifference}px)` }}
          >
            <RemoveIcon style={{ color: "#262626" }} />
          </IconButton>
        )}
        <div
          className={classes.wrapper}
          style={{
            minHeight: scaledHeight - 48 / scaleFactor,
            maxHeight: scaledHeight,
            minWidth: scaledWidth,
            width: scaledWidth,
          }}
        >
          {rowElements}
        </div>
        <div
          className={classes.rightPanel}
          style={{
            display: "flex",
            position: "absolute",
            top: 0,
            height: scaledHeight - shelfBottomHeight,
          }}
        >
          <img
            src={`${baseUrl}/images/item/bert/archive/image/frame_height_${height}_${frameModel}.svg`}
            className={classes.leftFrame1}
            style={{
              maxHeight: scaledHeight - shelfBottomHeight + 58 / scaleFactor,
              position: "absolute",
              right: -scaledWidth + shelfBottomHeight - (lastField ? 64 / scaleFactor : 32 / scaleFactor),
              marginTop: shelfBottomHeight - 48 / scaleFactor,
              zIndex: 14,
              display: "block",
            }}
          />
          {!rightSidePanel ? (
            <img
              src={`${baseUrl}/images/item/bert/archive/image/side_singlepanels_height_${height}_depth_${depth}_${sidepanelModel}.svg`}
              className={classes.rightSidePanel}
              style={{
                maxHeight: scaledHeight - shelfBottomHeight + 58 / scaleFactor,
                position: "absolute",
                marginTop: shelfBottomHeight,
                right: -scaledWidth - 8 / scaleFactor,
                zIndex: 13,
              }}
            />
          ) : (
            <div
              className={classes.rightSidePanel}
              style={{
                backgroundImage: `url(${baseUrl}/images/item/bert/archive/image/side_fullpanel_height_${height}_depth_${depth}.svg)`,
                minHeight: scaledHeight,
                minWidth: shelfBottomHeight,
                marginTop: 4,
                backgroundSize: "cover",
                position: "absolute",
                right: -scaledWidth,
                zIndex: 13,
              }}
            />
          )}
          <img
            src={`${baseUrl}/images/item/bert/archive/image/frame_height_${height}_${frameModel}.svg`}
            className={classes.leftFrame1}
            style={{
              maxHeight: scaledHeight - shelfBottomHeight + 58 / scaleFactor,
              position: "absolute",
              left: scaledWidth - 48 / scaleFactor,
              top: 16 / scaleFactor,
              display: "block",
            }}
          />
        </div>
        <div className={classes.shelfHeight}>
          <div>{t("common:shelfHeight")}</div>
          <div>{`${shelfHeight} mm`}</div>
        </div>
        <div className={classes.shelfWidth} style={{ width: scaledWidth - shelfWidthOffset }}>
          <div className={classes.arrow}>
            <img src={arrowUp} style={{ transform: "rotate(-90deg)" }} />
            <div style={{ width: "100%", height: 4.5, borderBottom: "1px solid #979797", marginLeft: -3 }} />
          </div>
          <div
            style={{
              marginLeft: widthContainerMargin,
              marginRight: widthContainerMargin,
              minWidth: widthContainerWidth,
            }}
          >{`${width} mm`}</div>
          <div className={classes.arrow}>
            <div style={{ width: "100%", height: 4.5, borderBottom: "1px solid #979797", marginRight: -3 }} />
            <img src={arrowDown} style={{ transform: "rotate(-90deg)" }} />
          </div>
        </div>
        {optionsLoaded && (
          <IconButton
            className={classes.iconButton}
            style={{ left: `calc(50% - 21px - ${shelfBottomHeightDifference}px)` }}
            onClick={(e) => {
              setShowFieldOptions(true);
            }}
          >
            <EditIcon style={{ color: "#262626" }} />
          </IconButton>
        )}

        {showFieldOptions && (
          <FieldOptionsDialog
            show={true}
            onClose={() => setShowFieldOptions(false)}
            width={widthIdentifier}
            leftSidePanel={leftSidePanel}
            rightSidePanel={rightSidePanel}
            supportBars={supportbarIdentifier}
            updateSupportBarsAmount={(val) => updateSupportBarsAmount(val)}
            updateWidth={(val) => updateWidth(val)}
            updateLeftSidePanel={(val) => updateLeftSidePanel(val)}
            updateRightSidePanel={(val) => updateRightSidePanel(val)}
          />
        )}
      </div>
    </div>
  );
};

export default FrontViewIsoArchive;
